import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail } from "../lib/api";
import { TEST_IDS } from "../constants/testIds";
import { Switch } from "../components/ui/switch";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Bell, BellOff, Send, ShieldAlert, Smartphone } from "lucide-react";
import {
  isSupported as isPushSupported,
  subscribe as subscribePush,
  unsubscribe as unsubscribePush,
  getCurrentSubscription,
  sendTestPush,
  fetchPushDiagnostics,
  resetAndResubscribe,
} from "../lib/push";

const PREF_KEYS = [
  "task_assigned",
  "task_updated",
  "task_commented",
  "task_due_soon",
  "mentioned",
  "dm_message",
  "invite_accepted",
  "billing_events",
  "daily_digest",
];

// Detect iOS Safari that is NOT running as an installed PWA. iOS Safari only
// exposes PushManager to standalone (home-screen) apps starting 16.4, so a
// regular tab has no PushManager and our previous UI hid the card entirely —
// which left users wondering where the "Send test" button went. This flag lets
// us render an explanatory hint instead of silently hiding.
function detectIosNeedsPwa() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  const isIos = /iP(hone|ad|od)/.test(ua);
  if (!isIos) return false;
  const standalone =
    window.navigator.standalone === true ||
    window.matchMedia?.("(display-mode: standalone)").matches;
  return !standalone;
}

export default function NotificationPrefs() {
  const { t } = useTranslation();
  const [prefs, setPrefs] = useState(null);
  const [pushState, setPushState] = useState({
    supported: false,
    permission: "default",
    subscribed: false,
    busy: false,
  });
  const [diagnostics, setDiagnostics] = useState(null);
  const [diagBusy, setDiagBusy] = useState(false);
  const [lastTest, setLastTest] = useState(null); // { delivered, subscription_count, failures[] }

  const refreshPushState = useCallback(async () => {
    const supported = isPushSupported();
    if (!supported) {
      setPushState((s) => ({ ...s, supported: false }));
      return;
    }
    const sub = await getCurrentSubscription();
    setPushState({
      supported: true,
      permission: Notification.permission,
      subscribed: !!sub,
      busy: false,
    });
  }, []);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/users/me/notification-prefs");
      setPrefs(data);
    })();
    refreshPushState();
  }, [refreshPushState]);

  const toggle = async (key) => {
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    try {
      const { data } = await api.patch("/users/me/notification-prefs", {
        prefs: next,
      });
      setPrefs(data);
      toast.success(t("notif.saved"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const togglePush = async () => {
    setPushState((s) => ({ ...s, busy: true }));
    try {
      if (pushState.subscribed) {
        await unsubscribePush();
        toast.success(t("push.disabled_toast"));
      } else {
        await subscribePush();
        toast.success(t("push.enabled_toast"));
      }
    } catch (err) {
      toast.error(err.message || String(err));
    } finally {
      await refreshPushState();
    }
  };

  const runTest = async () => {
    setPushState((s) => ({ ...s, busy: true }));
    try {
      const res = await sendTestPush();
      // Backend now returns detailed struct — surface it prominently so
      // the user sees WHY nothing arrives (0 subs, VAPID mismatch,
      // subscription pruned, etc). No more silent "we tried".
      setLastTest(res);
      if (res.delivered === 0) {
        if ((res.subscription_count || 0) === 0) {
          toast.warning(
            "Aktif push aboneliğin yok. Aşağıdan 'Bildirimleri Aç' ile yeniden abone ol.",
          );
        } else if ((res.failures || []).length > 0) {
          const first = res.failures[0];
          toast.error(
            `Push başarısız: ${first.reason} (${first.status})`,
          );
        } else {
          toast.warning(t("push.test_no_delivery"));
        }
      } else {
        toast.success(t("push.test_sent", { count: res.delivered }));
      }
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || err.message,
      );
    } finally {
      setPushState((s) => ({ ...s, busy: false }));
    }
  };

  const loadDiagnostics = async () => {
    setDiagBusy(true);
    try {
      const data = await fetchPushDiagnostics();
      setDiagnostics(data);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || err.message,
      );
    } finally {
      setDiagBusy(false);
    }
  };

  const handleReset = async () => {
    setPushState((s) => ({ ...s, busy: true }));
    try {
      await resetAndResubscribe();
      toast.success("Abonelik sıfırlandı ve yeniden kaydedildi.");
      await loadDiagnostics();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || err.message,
      );
    } finally {
      await refreshPushState();
    }
  };

  if (!prefs) {
    return <div className="text-sm text-zinc-500">{t("common.loading")}</div>;
  }

  const iosNeedsPwa = detectIosNeedsPwa();
  const blocked = pushState.permission === "denied";
  // Card is always visible so users can see what push does; button state
  // changes based on capability.
  const canSubscribe = pushState.supported && !blocked;

  return (
    <div className="space-y-8">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-zinc-500">
          {t("brand")}
        </div>
        <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight text-zinc-950 sm:text-4xl">
          {t("notif.title")}
        </h1>
        <p className="mt-1 text-sm text-zinc-500">{t("notif.subtitle")}</p>
      </div>

      <div
        className="max-w-2xl rounded-2xl border border-violet-200 bg-gradient-to-br from-white via-violet-50/30 to-pink-50/30 p-5"
        data-testid="push-card"
      >
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-gradient-to-br from-[#5B4CFB] via-[#7c5cf6] to-[#EC4899] text-white shadow-lg shadow-violet-500/25">
              {pushState.subscribed ? (
                <Bell className="h-5 w-5" />
              ) : (
                <BellOff className="h-5 w-5" />
              )}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-zinc-900">
                {t("push.card_title")}
              </div>
              <p className="mt-1 text-xs leading-relaxed text-zinc-600">
                {pushState.subscribed
                  ? t("push.card_desc_on")
                  : t("push.card_desc_off")}
              </p>
              {iosNeedsPwa && (
                <div
                  className="mt-2 flex items-start gap-1.5 rounded-lg bg-amber-50 px-2 py-1.5 text-[11px] text-amber-800"
                  data-testid="push-ios-hint"
                >
                  <Smartphone className="h-3.5 w-3.5 flex-none" />
                  <span>
                    <span className="font-semibold">{t("push.ios_hint_title")}:</span>{" "}
                    {t("push.ios_hint_desc")}
                  </span>
                </div>
              )}
              {!iosNeedsPwa && !pushState.supported && (
                <div
                  className="mt-2 flex items-start gap-1.5 rounded-lg bg-zinc-100 px-2 py-1.5 text-[11px] text-zinc-700"
                  data-testid="push-unsupported-hint"
                >
                  <ShieldAlert className="h-3.5 w-3.5 flex-none" />
                  <span>{t("push.unsupported_hint")}</span>
                </div>
              )}
              {blocked && (
                <div className="mt-2 flex items-start gap-1.5 rounded-lg bg-red-50 px-2 py-1.5 text-[11px] text-red-700">
                  <ShieldAlert className="h-3.5 w-3.5 flex-none" />
                  <span>{t("push.blocked_hint")}</span>
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center">
            {pushState.subscribed && (
              <Button
                variant="outline"
                size="sm"
                onClick={runTest}
                disabled={pushState.busy}
                data-testid="push-test-btn"
                className="gap-1 border-zinc-300 text-xs"
              >
                <Send className="h-3 w-3" />
                {t("push.send_test")}
              </Button>
            )}
            <Button
              onClick={togglePush}
              disabled={pushState.busy || !canSubscribe}
              data-testid="push-toggle-btn"
              className={
                pushState.subscribed
                  ? "border border-zinc-300 bg-white text-zinc-800 hover:bg-zinc-50"
                  : "bg-gradient-to-r from-[#5B4CFB] to-[#7c5cf6] text-white shadow-lg shadow-violet-500/25 hover:from-[#4f40f0] hover:to-[#6f4df0] disabled:opacity-60"
              }
            >
              {pushState.busy
                ? "…"
                : pushState.subscribed
                  ? t("push.disable")
                  : t("push.enable")}
            </Button>
          </div>
        </div>
      </div>

      {/* ─── Diagnostics panel — user-facing "why aren't my pushes arriving?" ─── */}
      <div
        className="max-w-2xl rounded-lg border border-zinc-200 bg-white p-5"
        data-testid="push-diagnostics-panel"
      >
        <div className="mb-3 flex items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-zinc-900">
              Bildirim tanılama
            </h2>
            <p className="mt-0.5 text-[11px] text-zinc-500">
              Test bildirimi ulaşmıyorsa buradan neden&apos;i gör.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={loadDiagnostics}
            disabled={diagBusy}
            data-testid="push-diagnostics-refresh"
            className="text-xs"
          >
            {diagBusy ? "…" : diagnostics ? "Yenile" : "Kontrol et"}
          </Button>
        </div>

        {diagnostics && (
          <div className="space-y-2 text-[12px]">
            <div className="flex items-center gap-2">
              <span className={diagnostics.push_enabled ? "text-emerald-600" : "text-rose-600"}>
                {diagnostics.push_enabled ? "✓" : "✗"}
              </span>
              <span>
                Sunucuda VAPID{" "}
                {diagnostics.push_enabled ? "yapılandırılmış" : "eksik (canlıda env değişkenleri gerekli)"}
                {diagnostics.vapid_public_key_prefix && (
                  <span className="ml-1 font-mono text-[10px] text-zinc-500">
                    ({diagnostics.vapid_public_key_prefix})
                  </span>
                )}
              </span>
            </div>
            {diagnostics.vapid?.private_key_source &&
              diagnostics.vapid.private_key_source !== "none" && (
                <div className="ml-5 text-[10px] text-zinc-500">
                  Private key kaynağı:{" "}
                  <span className="font-mono">{diagnostics.vapid.private_key_source}</span>
                </div>
              )}
            {diagnostics.vapid?.private_key_error_class && (
              <div
                className="rounded-md border border-rose-200 bg-rose-50 p-2 text-[11px] text-rose-900"
                data-testid="push-vapid-parse-error"
              >
                <b>VAPID private key okunamıyor:</b>
                <div className="mt-0.5 font-mono text-[10px]">
                  {diagnostics.vapid.private_key_error_class}
                </div>
                {diagnostics.vapid.private_key_error_hint && (
                  <div className="mt-0.5 text-[11px]">
                    {diagnostics.vapid.private_key_error_hint}
                  </div>
                )}
                <div className="mt-1">
                  Backend .env dosyasında <code>VAPID_PRIVATE_KEY_B64</code>{" "}
                  değişkenini (base64-encoded PEM) tanımlamak en güvenli yoldur.
                </div>
              </div>
            )}
            <div className="flex items-center gap-2">
              <span
                className={
                  diagnostics.subscription_count > 0 ? "text-emerald-600" : "text-amber-600"
                }
              >
                {diagnostics.subscription_count > 0 ? "✓" : "!"}
              </span>
              <span>
                {diagnostics.subscription_count} aktif push aboneliği bu hesap
                için kayıtlı
              </span>
            </div>
            {(diagnostics.subscriptions || []).map((s, i) => (
              <div
                key={i}
                className="ml-5 rounded border border-zinc-200 bg-zinc-50 px-2 py-1.5 text-[11px]"
              >
                <div className="font-mono text-zinc-700">{s.endpoint_host}</div>
                <div className="text-[10px] text-zinc-500">
                  {s.user_agent?.slice(0, 90) || "unknown UA"}
                </div>
              </div>
            ))}
            {pushState.subscribed && (
              <div className="mt-3 border-t border-zinc-100 pt-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleReset}
                  disabled={pushState.busy}
                  data-testid="push-reset-btn"
                  className="text-xs"
                >
                  Aboneliği sıfırla & yeniden kaydet
                </Button>
                <p className="mt-1 text-[10px] text-zinc-500">
                  &quot;Could not deserialize key data&quot; veya eski VAPID
                  anahtarı hatası alıyorsan bunu kullan. Tarayıcıdaki
                  aboneliği siler ve güncel sunucu anahtarıyla yeniden
                  oluşturur.
                </p>
              </div>
            )}
          </div>
        )}

        {lastTest && (
          <div className="mt-3 rounded-md border border-zinc-200 bg-zinc-50 p-2.5 text-[11px]">
            <div className="mb-1 font-semibold text-zinc-800">
              Son test sonucu
            </div>
            <div className="flex items-center gap-2">
              <span className={lastTest.delivered > 0 ? "text-emerald-700" : "text-rose-700"}>
                {lastTest.delivered} / {lastTest.subscription_count} teslim edildi
              </span>
              {lastTest.vapid_source && (
                <span className="text-[10px] text-zinc-500">
                  · imzalayan: <span className="font-mono">{lastTest.vapid_source}</span>
                </span>
              )}
            </div>
            {(lastTest.failures || []).map((f, i) => (
              <div key={i} className="ml-2 mt-1 text-rose-700">
                <div>
                  • {f.endpoint_host} → HTTP {f.status} · {f.reason}
                </div>
                {f.detail && (
                  <div className="ml-3 mt-0.5 break-all font-mono text-[10px] text-rose-500">
                    {f.detail}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {diagnostics && diagnostics.subscription_count === 0 && (
          <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-2.5 text-[11px] text-amber-900">
            <b>Hiç aboneliğin yok.</b> Bildirimleri Aç butonuna basıp
            tarayıcı izin dialog&apos;unda <b>İzin Ver</b> demen gerekiyor. İzin
            reddettiysen tarayıcı ayarlarından bildirimleri sıfırlayıp yeniden
            deneyebilirsin.
          </div>
        )}

        {diagnostics && !diagnostics.push_enabled && (
          <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 p-2.5 text-[11px] text-rose-900">
            <b>Sunucu tarafında VAPID key eksik.</b> Canlı ortamda
            (serpix.co) push için ortam değişkenleri gerekli:
            <code className="ml-1 rounded bg-white px-1 py-0.5 font-mono text-[10px]">
              VAPID_PUBLIC_KEY / VAPID_PRIVATE_KEY / VAPID_ADMIN_EMAIL
            </code>
            . Yeniden deploy ettikten sonra tekrar dene.
          </div>
        )}
      </div>

      <div className="max-w-2xl rounded-lg border border-zinc-200 bg-white">
        <ul className="divide-y divide-zinc-100">
          {PREF_KEYS.map((key) => (
            <li
              key={key}
              className="flex items-center justify-between gap-4 p-5"
              data-testid={TEST_IDS.notifRow(key)}
            >
              <div className="min-w-0">
                <div className="text-sm font-medium text-zinc-900">
                  {t(`notif.${key}`)}
                </div>
                <div className="text-xs text-zinc-500">
                  {t(`notif.${key}_desc`)}
                </div>
              </div>
              <Switch
                checked={!!prefs[key]}
                onCheckedChange={() => toggle(key)}
                data-testid={TEST_IDS.notifToggle(key)}
              />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
